import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
import { Check } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { Button, Input } from '../../components/ui';
import { useToast } from '../../components/ui/Toast';
import { checkPasswordStrength } from '../../utils/helpers';
import { 
  validateEmailForSignup, 
  checkSignupRateLimit, 
  recordSignupAttempt 
} from '../../utils/security';

const SignUpPage = () => {
  const { signUp, user } = useAuth();
  const navigate = useNavigate();
  const { showToast } = useToast();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // Redirect if already logged in
  React.useEffect(() => {
    if (user) {
      navigate('/', { replace: true });
    }
  }, [user, navigate]);

  const passwordStrength = checkPasswordStrength(password);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Check rate limiting
    const rateLimitCheck = checkSignupRateLimit();
    if (!rateLimitCheck.allowed) {
      const waitMinutes = Math.ceil(rateLimitCheck.waitTime / 60000);
      setError(`Too many signup attempts. Please try again in ${waitMinutes} minute${waitMinutes > 1 ? 's' : ''}.`);
      return;
    }

    // Validate email
    const emailValidation = validateEmailForSignup(email);
    if (!emailValidation.valid) {
      setError(emailValidation.error);
      return;
    }

    if (!agreedToTerms) {
      setError('Please agree to the Terms of Service and Privacy Policy');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    if (passwordStrength.label === 'weak') {
      setError('Please choose a stronger password with uppercase, lowercase, and numbers');
      return;
    }

    setIsLoading(true);
    recordSignupAttempt();

    try {
      await signUp(email, password);
      showToast('Account created! Please check your email to verify.', 'success');
      navigate('/signin');
    } catch (err) {
      setError(err.message || 'Failed to create account. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const benefits = [
    '10 free calculations every month',
    'Save and continue conversations',
    'Access from any device',
    'Detailed material breakdowns',
  ];

  const strengthColors = {
    weak: 'bg-red-500',
    medium: 'bg-amber-500',
    strong: 'bg-green-500',
  };

  return (
    <>
      <Helmet>
        <title>Sign Up Free - CalcBuildAI</title>
        <meta name="description" content="Create a free account to save calculations and get 10 free calculations every month." />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white flex items-center justify-center py-12 px-4">
        <div className="max-w-md w-full">
          {/* Logo */}
          <div className="text-center mb-8">
            <Link to="/" className="inline-flex items-center gap-2">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-lg">CB</span>
              </div>
              <span className="font-bold text-2xl text-gray-900">Calc<span className="text-blue-600">Build</span>AI</span>
            </Link>
          </div>

          {/* Card */}
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <h1 className="text-2xl font-bold text-gray-900 text-center mb-2">
              Create Your Account
            </h1>
            <p className="text-gray-600 text-center mb-8">
              Start calculating materials for free
            </p>

            {error && (
              <div className="bg-red-50 text-red-700 p-4 rounded-lg mb-6 text-sm">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <Input
                type="email"
                label="Email"
                name="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
              />

              <div>
                <Input
                  type="password"
                  label="Password"
                  name="password"
                  placeholder="Create a password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="new-password"
                  helperText="At least 6 characters"
                />
                
                {/* Password strength indicator */}
                {password.length > 0 && (
                  <div className="mt-2">
                    <div className="flex gap-1 mb-1">
                      <div className={`h-1 flex-1 rounded ${password.length > 0 ? strengthColors[passwordStrength] : 'bg-gray-200'}`} />
                      <div className={`h-1 flex-1 rounded ${passwordStrength !== 'weak' ? strengthColors[passwordStrength] : 'bg-gray-200'}`} />
                      <div className={`h-1 flex-1 rounded ${passwordStrength === 'strong' ? strengthColors[passwordStrength] : 'bg-gray-200'}`} />
                    </div>
                    <p className="text-xs text-gray-500 capitalize">
                      Password strength: {passwordStrength}
                    </p>
                  </div>
                )}
              </div>

              {/* Terms checkbox */}
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="terms"
                  checked={agreedToTerms}
                  onChange={(e) => setAgreedToTerms(e.target.checked)}
                  className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <label htmlFor="terms" className="text-sm text-gray-600">
                  I agree to the{' '}
                  <Link to="/terms" className="text-blue-600 hover:underline">
                    Terms of Service
                  </Link>{' '}
                  and{' '}
                  <Link to="/privacy" className="text-blue-600 hover:underline">
                    Privacy Policy
                  </Link>
                </label>
              </div>

              <Button
                type="submit"
                variant="primary"
                size="lg"
                fullWidth
                loading={isLoading}
              >
                Create Account
              </Button>
            </form>

            {/* Benefits */}
            <div className="mt-8 pt-6 border-t">
              <p className="text-sm font-semibold text-gray-900 mb-3">
                Free account includes:
              </p>
              <ul className="space-y-2">
                {benefits.map((benefit, idx) => (
                  <li key={idx} className="flex items-center gap-2 text-sm text-gray-600">
                    <Check className="w-4 h-4 text-green-500" />
                    {benefit}
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-6 text-center">
              <p className="text-gray-600">
                Already have an account?{' '}
                <Link to="/signin" className="text-blue-600 hover:text-blue-700 font-medium">
                  Sign in
                </Link>
              </p>
            </div>
          </div>

          {/* Back to home */}
          <div className="mt-6 text-center">
            <Link to="/" className="text-gray-500 hover:text-gray-700 text-sm">
              ← Back to home
            </Link>
          </div>
        </div>
      </div>
    </>
  );
};

export default SignUpPage;
